import type { Color, Frame, Layer } from "#canvas/types.ts";
import { COLS, ROWS } from "#puf/constants.ts";
import type { StateId } from "@pixelpuf/schemas/state";
import { StateIdSchema } from "@pixelpuf/schemas/state";

export function isStateId(v: string): v is StateId {
  return StateIdSchema.safeParse(v).success;
}

export function emptyData(rows: number = ROWS, cols: number = COLS): Color[][] {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ""));
}

export function emptyFrameLayers(rows: number = ROWS, cols: number = COLS): Frame {
  return [{ id: "L1", visible: true, data: emptyData(rows, cols) }];
}

export function makeThumb(
  layers: readonly Layer[],
  cols: number = COLS,
  rows: number = ROWS,
): string {
  const canvas = document.createElement("canvas");
  canvas.width = cols;
  canvas.height = rows;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  for (const layer of layers) {
    if (!layer.visible) continue;
    for (let r = 0; r < layer.data.length; r++) {
      const row = layer.data[r];
      if (!row) continue;
      for (let c = 0; c < row.length; c++) {
        const color = row[c];
        if (!color) continue;
        ctx.fillStyle = color;
        ctx.fillRect(c, r, 1, 1);
      }
    }
  }
  return canvas.toDataURL("image/png");
}

const CANVAS_BG = { r: 0x1f, g: 0x21, b: 0x25 };

export function blendWithBg(hex: string, alpha: number): string {
  if (hex.length < 7) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return hex;
  const mix = (c: number, bg: number) => Math.round(c * alpha + bg * (1 - alpha));
  const hh = (n: number) => n.toString(16).padStart(2, "0");
  return "#" + hh(mix(r, CANVAS_BG.r)) + hh(mix(g, CANVAS_BG.g)) + hh(mix(b, CANVAS_BG.b));
}

export function makeSpritesheet(
  allFrames: readonly Frame[],
  cols: number = COLS,
  rows: number = ROWS,
): string {
  const canvas = document.createElement("canvas");
  canvas.width = cols * allFrames.length;
  canvas.height = rows;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  allFrames.forEach((frame, i) => {
    const xOff = i * cols;
    for (const layer of frame) {
      if (!layer.visible) continue;
      for (let r = 0; r < layer.data.length; r++) {
        const row = layer.data[r];
        if (!row) continue;
        for (let c = 0; c < row.length; c++) {
          const color = row[c];
          if (!color) continue;
          ctx.fillStyle = color;
          ctx.fillRect(xOff + c, r, 1, 1);
        }
      }
    }
  });
  return canvas.toDataURL("image/png");
}

export function downloadDataURL(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
